import { useCallback, useMemo, useState } from 'react'
import type { Slot, Task } from './types'
import { searchTasks } from './search'
import { useI18n } from './i18n'

type Props = {
  slot: Slot
  tasks: Task[]
  byId: Map<string, Task>
  /** カッパコンテナに要るタスク（前提チェーン込み） */
  kappaIds: Set<string>
  traders: string[]
  /** マップの normalizedName。検索の絞り込み候補 */
  maps: string[]
  /** normalizedName → 画面に出す名前 */
  mapLabels: Record<string, string>
  onRename: (name: string) => void
  onRegister: (taskId: string) => void
  onUnregister: (taskId: string) => void
}

const MAX_RESULTS = 40

/**
 * 1人ぶんの列。上に登録済み、下に検索欄という仕様どおりの並び。
 *
 * 登録欄の番号はこの列の登録順（1始まり）で、マップタブの一覧・マップ上の図形に付く
 * 番号とは別系統。あちらは表示中のマップごとに振り直されるため、共通にはできない。
 */
export function SlotPanel({
  slot,
  tasks,
  byId,
  kappaIds,
  traders,
  maps,
  mapLabels,
  onRename,
  onRegister,
  onUnregister,
}: Props) {
  // このファイルの `t` はタスク。訳語は `msg` で引く
  const { t: msg, lang } = useI18n()
  const [trader, setTrader] = useState('')
  const [name, setName] = useState('')
  const [map, setMap] = useState('')
  /**
   * A→Z 表示。**この列の見た目だけ**で、共有データ（`slot.taskIds` の順）は動かさない。
   * 登録欄の番号は登録順のままなので、並べ替えても「何件目に登録したか」は失われない。
   */
  const [sorted, setSorted] = useState(false)

  const byName = useCallback(
    (a: Task, b: Task) => a.name.localeCompare(b.name, lang),
    [lang],
  )

  const results = useMemo(() => {
    const hits = searchTasks(tasks, { trader, name, map })
    // 打ち止めは並べ替えより先。「A→Z にしたら出ていた候補が消えた」を起こさない
    const shown = hits.slice(0, MAX_RESULTS)
    return { total: hits.length, shown: sorted ? shown.slice().sort(byName) : shown }
  }, [tasks, trader, name, map, sorted, byName])

  /** 番号は登録順（`i + 1`）。A→Z にしても番号は付け替えない */
  const registered = useMemo(() => {
    const rows = slot.taskIds
      .map((id, i) => ({ task: byId.get(id), no: i + 1 }))
      .filter((r): r is { task: Task; no: number } => !!r.task)
    return sorted ? rows.slice().sort((a, b) => byName(a.task, b.task)) : rows
  }, [slot.taskIds, byId, sorted, byName])

  const hasQuery = Boolean(trader || name.trim() || map)

  return (
    <section className="slot">
      <header className="slot__head">
        <span className="slot__badge">{slot.id}</span>
        <input
          className="slot__name"
          value={slot.name}
          onChange={(e) => onRename(e.target.value.slice(0, 40))}
          placeholder={msg('slot.name.placeholder')}
          aria-label={msg('slot.name.aria', { id: slot.id })}
        />
        <span className="slot__count">{msg('slot.count', { n: registered.length })}</span>
      </header>

      {/* 登録済み（検索欄の上） */}
      <ul className="slot__registered">
        {registered.length === 0 && <li className="slot__empty">{msg('slot.none')}</li>}
        {registered.map(({ task: t, no }) => {
          return (
            <li key={t.id} className="reg">
              <span className="reg__no">{no}</span>
              <span className="reg__name" title={t.name}>
                {t.name}
              </span>
              {kappaIds.has(t.id) && (
                <span className="reg__kappa" title={msg('task.kappa.title')}>
                  {msg('task.kappa')}
                </span>
              )}
              <button
                type="button"
                className="reg__remove"
                onClick={() => onUnregister(t.id)}
                aria-label={msg('slot.unregister', { name: t.name })}
              >
                ×
              </button>
            </li>
          )
        })}
      </ul>

      {/* 検索 */}
      <div className="slot__search">
        <div className="slot__filters">
          <select value={trader} onChange={(e) => setTrader(e.target.value)} aria-label={msg('slot.trader')}>
            <option value="">{msg('slot.trader')}</option>
            {traders.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select value={map} onChange={(e) => setMap(e.target.value)} aria-label={msg('slot.map')}>
            <option value="">{msg('slot.map')}</option>
            {maps.map((m) => (
              <option key={m} value={m}>
                {mapLabels[m] ?? m}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`slot__sort${sorted ? ' slot__sort--on' : ''}`}
            onClick={() => setSorted((v) => !v)}
            aria-pressed={sorted}
            title={msg('slot.sort.title')}
          >
            {msg('slot.sort')}
          </button>
        </div>
        <input
          className="slot__q"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={msg('slot.query.placeholder')}
          aria-label={msg('slot.query.aria')}
        />

        <ul className="slot__results">
          {!hasQuery && <li className="slot__empty">{msg('slot.hint')}</li>}
          {hasQuery && results.total === 0 && <li className="slot__empty">{msg('slot.noHit')}</li>}
          {results.shown.map((t) => {
            const already = slot.taskIds.includes(t.id)
            return (
              <li key={t.id}>
                <button
                  type="button"
                  className={`hit${already ? ' hit--on' : ''}`}
                  onClick={() => (already ? onUnregister(t.id) : onRegister(t.id))}
                  title={t.objectives[0]?.description ?? t.name}
                >
                  <span className="hit__name">{t.name}</span>
                  <span className="hit__meta">
                    {t.trader}
                    {t.maps.length > 0 && ` · ${t.maps.map((m) => mapLabels[m] ?? m).join(' / ')}`}
                    {kappaIds.has(t.id) && (
                      <span className="hit__kappa" title={msg('task.kappa.title')}>
                        {msg('task.kappa')}
                      </span>
                    )}
                  </span>
                  <span className="hit__act">{already ? msg('slot.added') : msg('slot.add')}</span>
                </button>
              </li>
            )
          })}
          {results.total > MAX_RESULTS && (
            <li className="slot__empty">{msg('slot.more', { n: results.total - MAX_RESULTS })}</li>
          )}
        </ul>
      </div>
    </section>
  )
}
